/**
 * Auth state. Boots from GET /auth/session, exposes the OTP flow, and the
 * current user. Passwordless: initiate → verify → cookie set by the worker.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { User } from '@fitplatform/shared';
import { api } from './api';

interface AuthState {
  user: User | null;
  loading: boolean;
  initiate: (email: string) => Promise<{ auth_request_id: string }>;
  verify: (authRequestId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Re-fetch the current user (after a profile edit). */
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<{ user: User | null }>('/auth/session')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const initiate = async (email: string) => {
    return api.post<{ auth_request_id: string; expires_in: number }>('/auth/otp/initiate', {
      email,
    });
  };

  const verify = async (authRequestId: string, code: string) => {
    const r = await api.post<{ user: User }>('/auth/otp/verify', {
      auth_request_id: authRequestId,
      code,
    });
    setUser(r.user);
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  const refresh = async () => {
    try {
      const r = await api.get<{ user: User | null }>('/auth/session');
      setUser(r.user);
    } catch {
      /* keep current user on transient failure */
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, initiate, verify, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
